/**
 * LtvWidget - Виджет LTV (Lifetime Value)
 * ========================================
 * 
 * Показывает график LTV по дням от регистрации
 * с визуализацией полных/неполных данных
 * 
 * Формула: LTV = Накопительный доход / Количество установок
 */

import React from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { Card, CardContent } from '../ui/card';
import { Line, XAxis, YAxis, CartesianGrid, ReferenceLine, Area, ComposedChart } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { 
  DollarSign, 
  Users,
  AlertCircle
} from 'lucide-react';
import { ltv as ltvSpecs } from '@ggstat/chart-specs';

// Ряды и ключевые дни — общие с MCP (shared/chart-specs/specs/ltv.json)
const [FULL_SERIES, PARTIAL_SERIES] = ltvSpecs.ltv.series;
const BREAKEVEN_COLOR = ltvSpecs.ltv.breakevenColor;
const KEY_DAYS = ltvSpecs.ltv.keyDays;

/**
 * Компонент статистики
 */
function StatCard({ icon: Icon, label, value, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Компонент графика LTV
 */
function LtvChart({ data, fullDataDays, cohortCost }) {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      const dataPoint = payload[0].payload;
      const isFullData = dataPoint.day <= fullDataDays;
      
      const roas = cohortCost > 0 ? dataPoint.cumulativeRevenue / cohortCost : 0;
      
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-2">День {label}</p>
          <div className="space-y-1">
            <p className="text-sm">
              <span className="text-muted-foreground">LTV:</span>{' '}
              <span className="font-bold">{MetricsService.formatCurrency(dataPoint.ltv)}</span>
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">Накопительный доход:</span>{' '}
              {MetricsService.formatCurrency(dataPoint.cumulativeRevenue)}
            </p>
            <p className="text-sm">
              <span className="text-muted-foreground">ROAS:</span>{' '}
              <span className={`font-bold ${roas >= 1 ? 'text-green-600' : 'text-amber-600'}`}>
                {roas.toFixed(2)}x
              </span>
            </p>
          </div>
          {!isFullData && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Неполные данные
              </p>
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Находим точку окупаемости в полных данных
  const fullData = data.slice(0, fullDataDays + 1);
  const breakevenDay = fullData.findIndex(point => {
    const roas = cohortCost > 0 ? point.cumulativeRevenue / cohortCost : 0;
    return roas >= 1.0;
  });
  
  const hasBreakeven = breakevenDay >= 0;
  const breakevenPercent = hasBreakeven && fullDataDays > 0 
    ? (breakevenDay / fullDataDays) * 100 
    : 100;
  
  return (
    <ResponsiveContainer width="100%" height={500}>
      <ComposedChart data={data}>
        <defs>
          {/* Градиент для полных данных */}
          {hasBreakeven ? (
            // Если есть окупаемость: красный -> желтый -> зеленый
            <linearGradient id="ltvGradientFull" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4}/>
              <stop offset={`${breakevenPercent}%`} stopColor="#eab308" stopOpacity={0.3}/>
              <stop offset={`${breakevenPercent}%`} stopColor="#22c55e" stopOpacity={0.2}/>
              <stop offset="100%" stopColor="#22c55e" stopOpacity={0.5}/>
            </linearGradient>
          ) : (
            // Если нет окупаемости: красный -> желтый
            <linearGradient id="ltvGradientFull" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4}/>
              <stop offset="100%" stopColor="#eab308" stopOpacity={0.3}/>
            </linearGradient>
          )}
          {/* Серый для неполных данных */}
          <linearGradient id="ltvGradientGray" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.3}/>
            <stop offset="95%" stopColor="#9ca3af" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis 
          dataKey="day" 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          label={{ value: 'Дни от регистрации', position: 'insideBottom', offset: -5 }}
        />
        <YAxis 
          className="text-xs"
          tick={{ fill: 'currentColor' }}
          tickFormatter={(value) => MetricsService.formatCurrency(value)}
          label={{ value: 'LTV (USD)', angle: -90, position: 'insideLeft' }}
        />
        <Tooltip content={<CustomTooltip />} />
        
        {/* Линия разделения полных/неполных данных */}
        {fullDataDays > 0 && fullDataDays < data.length - 1 && (
          <ReferenceLine 
            x={fullDataDays} 
            stroke={PARTIAL_SERIES.color}
            strokeDasharray="5 5"
            label={{ 
              value: 'Неполные данные →', 
              position: 'top',
              fill: PARTIAL_SERIES.color,
              fontSize: 12
            }}
          />
        )}
        
        {/* Заливка для полных данных */}
        <Area
          type="monotone"
          dataKey={(entry) => entry.day <= fullDataDays ? entry.ltv : null}
          fill="url(#ltvGradientFull)"
          stroke="none"
          isAnimationActive={false}
          connectNulls
        />
        
        {/* Заливка для неполных данных */}
        <Area
          type="monotone"
          dataKey={(entry) => entry.day >= fullDataDays ? entry.ltv : null}
          fill="url(#ltvGradientGray)"
          stroke="none"
          isAnimationActive={false}
          connectNulls
        />
        
        {/* Основная линия LTV */}
        <Line 
          type="monotone" 
          dataKey="ltv" 
          stroke={FULL_SERIES.color}
          strokeWidth={3}
          dot={(props) => {
            const { cx, cy, payload, key } = props;
            const isFullData = payload.day <= fullDataDays;
            const roas = cohortCost > 0 ? payload.cumulativeRevenue / cohortCost : 0;
            const isBreakeven = roas >= 1.0;
            
            // Точка окупаемости - крупная зеленая
            if (isBreakeven && payload.day > 0) {
              const prevPoint = data[payload.day - 1];
              const prevRoas = cohortCost > 0 ? prevPoint.cumulativeRevenue / cohortCost : 0;
              if (prevRoas < 1.0) {
                return (
                  <circle
                    key={key || `dot-${payload.day}`}
                    cx={cx}
                    cy={cy}
                    r={6}
                    fill={BREAKEVEN_COLOR}
                    stroke="white"
                    strokeWidth={2}
                  />
                );
              }
            }
            
            return (
              <circle
                key={key || `dot-${payload.day}`}
                cx={cx}
                cy={cy}
                r={2}
                fill={isFullData ? FULL_SERIES.color : PARTIAL_SERIES.color}
                stroke="white"
                strokeWidth={1}
              />
            );
          }}
          activeDot={{ r: 4 }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

/**
 * Основной компонент виджета
 */
export function LtvWidget() {
  // Читаем состояние чекбокса из localStorage
  const [shouldApplyFees, setShouldApplyFees] = React.useState(() => {
    const stored = localStorage.getItem('applyPlatformFees');
    return stored === 'true';
  });
  
  // Слушаем изменения в localStorage (когда чекбокс меняется в Sidebar)
  React.useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('applyPlatformFees');
      setShouldApplyFees(stored === 'true');
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('platformFeesChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('platformFeesChanged', handleStorageChange);
    };
  }, []);
  
  // Загружаем данные используя хук
  const widgetData = useWidgetData(
    async (filters, options) => {
      const response = await MetricsService.getLtvData(filters, options);
      return response;
    },
    []
  );
  
  // Используем хелпер для BaseWidget
  const widgetProps = useBaseWidget(widgetData);
  let { data: ltvData } = widgetProps;
  
  // Чистый доход: серверные inappsAndroidNet / inappsIosNet вместо gross, AdMon не меняется
  if (shouldApplyFees && ltvData) {
    const { data, ...rest } = ltvData;

    if (data && Array.isArray(data)) {
      const netRows = MetricsService.applyNetRevenue(data, true);

      // Пересчитываем LTV с новыми доходами
      let cumulativeRevenue = 0;
      const adjustedData = netRows.map((item) => {
        const inappsAndroid = item.inappsAndroid || 0;
        const inappsIos = item.inappsIos || 0;
        const admonRevenue = item.admonRevenue || 0;

        const totalRevenue = inappsAndroid + inappsIos + admonRevenue;
        cumulativeRevenue += totalRevenue;
        
        const ltv = rest.totalCohortSize > 0 ? cumulativeRevenue / rest.totalCohortSize : 0;
        
        return {
          ...item,
          inappsAndroid,
          inappsIos,
          ltv: Math.round(ltv * 100) / 100,
          cumulativeRevenue: Math.round(cumulativeRevenue * 100) / 100
        };
      });
      
      ltvData = {
        ...rest,
        data: adjustedData
      };
    }
  }
  
  // Вычисляем ключевые метрики
  const keyMetrics = React.useMemo(() => {
    if (!ltvData?.data || ltvData.data.length === 0) return null;
    
    const cohortCost = ltvData.totalCohortCost || 0;
    
    // Ключевые дни для ROAS и LTV
    const keyDays = KEY_DAYS;
    const metricsData = keyDays.map(day => {
      const dayData = ltvData.data.find(d => d.day === day);
      const revenue = dayData?.cumulativeRevenue || 0;
      const roas = cohortCost > 0 ? revenue / cohortCost : 0;
      const ltv = dayData?.ltv || 0;
      return {
        day,
        roas,
        ltv,
        revenue,
        available: !!dayData
      };
    });
    
    const lastDay = ltvData.data[ltvData.data.length - 1];
    
    return {
      cohortSize: ltvData.totalCohortSize || 0,
      cohortCost: cohortCost,
      maxDays: ltvData.maxDays || 0,
      cumulativeRevenue: lastDay?.cumulativeRevenue || 0,
      currentLtv: lastDay?.ltv || 0,
      metricsData
    };
  }, [ltvData]);
  
  return (
    <BaseWidget
      {...widgetProps}
      title="LTV Analysis"
      subtitle={ltvData ? `Cohort: ${ltvData.cohortDateRange?.start} - ${ltvData.cohortDateRange?.end}` : null}
      emptyMessage="No LTV data available for selected period"
      skeletonRows={8}
    >
      <div className="space-y-6">
        {/* Основная информация о когорте */}
        {keyMetrics && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <StatCard
              icon={Users}
              label="Размер когорты"
              value={keyMetrics.cohortSize.toLocaleString()}
              subtitle={`${keyMetrics.maxDays} дней данных`}
            />
            <StatCard
              icon={DollarSign}
              label="Cohort Cost"
              value={MetricsService.formatCurrency(keyMetrics.cohortCost)}
              subtitle="Стоимость привлечения"
            />
            <StatCard
              icon={DollarSign}
              label={`LTV День ${keyMetrics.maxDays}`}
              value={MetricsService.formatCurrency(keyMetrics.currentLtv)}
              subtitle="Текущий LTV"
            />
            <StatCard
              icon={DollarSign}
              label="Накопительный доход"
              value={MetricsService.formatCurrency(keyMetrics.cumulativeRevenue)}
              subtitle="Всего заработано"
            />
            <StatCard
              icon={DollarSign}
              label="ROAS когорты"
              value={`${keyMetrics.cohortCost > 0
                ? ((keyMetrics.cumulativeRevenue / keyMetrics.cohortCost) * 100).toFixed(1)
                : '0.0'}%`}
              subtitle="Окупаемость"
            />
          </div>
        )}
        
        {/* Таблица метрик по ключевым дням */}
        {keyMetrics && (
          <Card>
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">День</th>
                      {keyMetrics.metricsData.map(item => (
                        <th key={item.day} className="text-center py-2 px-2 font-medium text-muted-foreground">
                          D{item.day}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 px-2 font-medium">ROAS</td>
                      {keyMetrics.metricsData.map(item => (
                        <td 
                          key={`roas-${item.day}`}
                          className={`text-center py-2 px-2 font-bold ${
                            !item.available ? 'text-muted-foreground' : 
                            item.roas >= 1 ? 'text-green-600' : 'text-amber-600'
                          }`}
                        >
                          {item.available ? `${item.roas.toFixed(2)}x` : '-'}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t">
                      <td className="py-2 px-2 font-medium">LTV</td>
                      {keyMetrics.metricsData.map(item => (
                        <td 
                          key={`ltv-${item.day}`}
                          className="text-center py-2 px-2 font-medium"
                        >
                          {item.available ? MetricsService.formatCurrency(item.ltv) : '-'}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* График LTV */}
        {ltvData?.data && ltvData.data.length > 0 && keyMetrics && (
          <div className="mt-6">
            <LtvChart 
              data={ltvData.data} 
              fullDataDays={ltvData.fullDataDays}
              cohortCost={keyMetrics.cohortCost}
            />
          </div>
        )}
        
      </div>
    </BaseWidget>
  );
}

export default LtvWidget;
